import { warn, debug } from "rtUtils/logger";
import getGlobal from "rtUtils/getGlobal";
import { VIEWERS } from "../consts";
import { getCloudFromConfig } from "../utils";

const EVENTS = {
	THREE_DETAILS_LOAD: "tag-3d-details-load",
	THREE_VIEWER_LOAD: "tag-3d-viewer-load",
	THREE_VIEWER_MODEL_LOAD: "tag-3d-viewer-model-load",
	THREE_VIEWER_INTERACTION: "tag-3d-viewer-interaction",
	THREE_VIEWER_MODEL_ERROR: "tag-3d-viewer-model-error",
	THREE_VIEWER_SCREENSHOT: "tag-3d-viewer-screenshot",
};

const VIEWER_TO_EVENTS_MAP = {
	"VIEWER_INIT": EVENTS.THREE_VIEWER_LOAD,
	"MODEL_LOADED": EVENTS.THREE_VIEWER_MODEL_LOAD,
	"ZOOM_INTERACTION": EVENTS.THREE_VIEWER_INTERACTION,
	"MODEL_ERROR": EVENTS.THREE_VIEWER_MODEL_ERROR,
	"SCREENSHOT": EVENTS.THREE_VIEWER_SCREENSHOT,
};

const root = getGlobal();

const reportFetchError = (id, response, triggerWithAnalytics) => {
	warn("Cloudinary 3D - 3D VIEWER - failed to retrieve model details", { id, response });

	triggerWithAnalytics(EVENTS.THREE_DETAILS_LOAD, {...response, error: true });

	return {
		response,
	};
};

const retrieveModelDetails = (id, getConfig, triggerWithAnalytics) => {
	const config = getConfig();
	const { apiUrl, defaults } = config;
	const url = `${apiUrl}3DViewer?sku=${id || defaults?.product}&${config.account ? `accountName=${config.account}` : `cloudName=${getCloudFromConfig(config)}`}`;

	debug("Cloudinary 3D - 3D VIEWER - fetching model details", { url });

	return fetch(url)
		.then((response) => {
			const { status, statusText } = response;

			debug("Cloudinary 3D - 3D VIEWER - retrieved response for model details", {
				status,
				statusText,
			});

			return status === 200 ?
				response.json()
					.then((jsonRes) => ({ jsonRes, response })) :
				reportFetchError(id, response, triggerWithAnalytics);
		})
		.then(({ jsonRes, response }) => {
			return {
				success: !!jsonRes,
				data: jsonRes,
			};
		});
};

const getViewerConfig = (container, viewerData, viewerConfig) => ({
	...(container ? { container } : {}),
	cloudName: viewerData.cloudName,
	modelUrl: viewerData.modelUrl,
	...{
		...viewerConfig,
		renderer: {
			...viewerData.renderOptions,
			...viewerConfig.renderer,
		},
	},
});

const renderViewerInContainer = (container, viewerData, viewerConfig, triggerWithAnalytics) => {
	container.innerHTML = "";
	const viewer = root.initDimensionsViewer(getViewerConfig(container, viewerData, viewerConfig));

	viewer.on((e) => {
		if (e.type === root.initDimensionsViewer.EVENTS.ANALYTICS) {
			const viewerAnalyticsEvent = VIEWER_TO_EVENTS_MAP[e.data.type];
			const error = e.data.type === "MODEL_ERROR";
			if (viewerAnalyticsEvent) {
				triggerWithAnalytics(viewerAnalyticsEvent, { ...e.data, error });
			}
		}

		if (e.type === root.initDimensionsViewer.EVENTS.SCREENSHOT) {
			const viewerAnalyticsEvent = VIEWER_TO_EVENTS_MAP[e.type];
			if (viewerAnalyticsEvent) {
				triggerWithAnalytics(viewerAnalyticsEvent, { ...e.data });
			}
		}
	});

	return viewer;
};

const getModelUrlFromData = (data, config) => {
	let optimizedUrl;

	if (config.threeDViewer?.useOptimized === true || data.useOptimized === true) {
		optimizedUrl = data.optimizations?.[0]?.url;

		if (optimizedUrl) {
			debug("Cloudinary 3D - 3D VIEWER - using optimized URL - ", optimizedUrl);
		}
	}

	return optimizedUrl || data.modelURL;
};

const getViewerData = async (id, container, loadedData, getConfig, triggerWithAnalytics) => {
	const config = getConfig();
	const cloudName = getCloudFromConfig(config);

	let viewerData;

	if (loadedData[id]) {
		//already retrieved data for this product
		viewerData = loadedData[id];
		debug("Cloudinary 3D - 3D VIEWER - reusing data already retrieved for product: " + id, { viewerData });
	} else {
		debug("Cloudinary 3D - 3D VIEWER - retrieving data for product: " + id, { viewerData });
		//need to get from API
		const details = await retrieveModelDetails(id, getConfig, triggerWithAnalytics);

		if (details.success) {
			let serverRendererProps = {};

			try {
				serverRendererProps = JSON.parse(details.data.renderOptions);
			} catch (ex) {
				debug("Cloudinary 3D - 3D VIEWER - failed to parse render options from server");
			}

			const modelUrl = getModelUrlFromData(details.data, config);

			//normalize config from server
			viewerData = loadedData[id] = {
				modelUrl: modelUrl,
				renderOptions: serverRendererProps,
			};
		}
	}

	return { ...viewerData, cloudName };
};

const loadContainerViewer = async (container, props, getConfig, loadedData, triggerWithAnalytics) => {
	const id = props.id;
	const config = getConfig();
	const viewerConfig = config.threeDViewer.viewer || {};
	let instance;

	const viewerData  = await getViewerData(id, container, loadedData, getConfig, triggerWithAnalytics);

	if (viewerData) {
		instance = renderViewerInContainer(
			container,
			viewerData,
			viewerConfig,
			triggerWithAnalytics,
		);
	} else {
		console.warn("Cloudinary 3D - failed to load 3D Viewer");
	}

	return instance;
};

const init3dViewer = ({ getConfig }, { triggerWithAnalytics }) => {
	const store = new WeakMap();
	const loadedData = {};

	return {
		type: VIEWERS.THREE_D,

		render: (containers) => {
			if (root.initDimensionsViewer) {
				containers.forEach(async ({ container, containerProps }) => {
					const instance = await loadContainerViewer(
						container,
						containerProps,
						getConfig,
						loadedData,
						triggerWithAnalytics,
					);

					//save viewer instance for later access
					if (instance) {
					    store.set(container, instance);
					 }
				});
			} else {
				//cant find tag init method
				warn(`Cloudinary 3D 3D viewer was not loaded! 
				Add a script with src: https://dimensions-3d-viewer.cloudinary.com/all.js`);
			}
		},

		update: (containers) => {
			containers.forEach(async ({ container, containerProps }) => {
				const instance = store.get(container);

				if (instance) {
					//need to fetch details in case product details change
					const viewerData  = await getViewerData(containerProps.id, container, loadedData, getConfig, triggerWithAnalytics);
					const viewerConfig = {
						...(getConfig().threeDViewer.viewer || {}),
						...containerProps,
					};

					instance.update(getViewerConfig(null, viewerData, viewerConfig));
				} else {
					warn(`Cloudinary 3D viewer instance not found for container`, container);
				}
			});
		},

		remove: (containers) => {
			//TODO: use 3d-viewer remove api when its available
			containers.forEach((container) => {
				const iframe = container.querySelector("iframe");
				if (iframe) {
					container.removeChild(iframe);
				}
			});
		},

		getConfig: (container) => {
			const instance = store.get(container);
			const config = instance?.getConfig();

			if (!config) {
				warn(`Cloudinary 3D viewer instance not found for container`, container);
			}

			return config;
		},
	};
};

export default init3dViewer;
